import React, { useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { Copy, Download, Share2 } from "lucide-react";

/**
 * Dialog que muestra al chatter un resumen del cierre de turno
 * listo para copiar/pegar en WhatsApp + un botón para descargar
 * el mismo resumen como imagen (canvas).
 */
export default function WhatsappReportDialog({ open, onClose, report, chatterName }) {
  const canvasRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const messageText = useMemo(() => {
    if (!report) return "";
    const models = report._models || [];
    const sbm = report.sales_by_model || {};
    const fmtDate = (d) => {
      try {
        const [y, m, day] = (d || "").split("-");
        return `${day}/${m}/${y}`;
      } catch {
        return d || "";
      }
    };
    const total = Object.values(sbm).reduce((s, v) => s + (parseFloat(v) || 0), 0);
    const moneyTotal = Math.max(parseFloat(report.money_collected) || 0, total);

    const lines = [];
    lines.push("📊 *CIERRE DE TURNO — JG MODELS*");
    lines.push("");
    lines.push(`👤 Chatter: *${chatterName || "—"}*`);
    lines.push(`📅 Fecha: ${fmtDate(report.date)}`);
    lines.push(`⏱️ Horas trabajadas: *${report.hours_worked || 0}h*`);
    lines.push("");
    lines.push(`💰 *Total recaudado: $${moneyTotal.toFixed(2)}*`);

    // Desglose por categoría (solo si hay datos)
    const subs = parseFloat(report.subs_amount) || 0;
    const msgs = parseFloat(report.messages_amount) || 0;
    const tips = parseFloat(report.tips_amount) || 0;
    if (subs + msgs + tips > 0) {
      lines.push("");
      lines.push("📂 *Categorías:*");
      if (subs > 0) lines.push(`   • Subs: $${subs.toFixed(2)}`);
      if (msgs > 0) lines.push(`   • Mensajes: $${msgs.toFixed(2)}`);
      if (tips > 0) lines.push(`   • Tips: $${tips.toFixed(2)}`);
    }

    // Distribución por modelo
    const sbmEntries = Object.entries(sbm).filter(([, v]) => (parseFloat(v) || 0) > 0);
    if (sbmEntries.length > 0) {
      lines.push("");
      lines.push("👑 *Distribución por modelo:*");
      sbmEntries
        .sort((a, b) => (parseFloat(b[1]) || 0) - (parseFloat(a[1]) || 0))
        .forEach(([mid, amt]) => {
          const modelName = models.find((m) => m.id === mid)?.name || mid;
          lines.push(`   • ${modelName}: $${(parseFloat(amt) || 0).toFixed(2)}`);
        });
    }

    // Notas próximo turno
    if (report.notas_proximo_turno && report.notas_proximo_turno.trim()) {
      lines.push("");
      lines.push("📌 *Notas para el próximo turno:*");
      lines.push(report.notas_proximo_turno.trim());
    }

    lines.push("");
    lines.push("_Generado desde el CRM de JG Models_");
    return lines.join("\n");
  }, [report, chatterName]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(messageText);
      toast.success("Mensaje copiado al portapapeles");
    } catch (e) {
      toast.error("No se pudo copiar — selecciona el texto manualmente");
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ text: messageText, title: "Cierre de turno JG Models" });
      } catch {
        // user cancelled
      }
    } else {
      // Fallback: abrir WhatsApp Web
      const url = `https://wa.me/?text=${encodeURIComponent(messageText)}`;
      window.open(url, "_blank");
    }
  };

  /**
   * Genera una imagen PNG con el resumen formateado.
   * Se dibuja sobre un canvas con tipografía simple y se descarga.
   */
  const generateImage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext("2d");

    const padding = 32;
    const lineHeight = 28;
    const titleHeight = 50;
    const lines = messageText
      .split("\n")
      .map((l) => l.replace(/\*/g, "")); // sin asteriscos en imagen

    const width = 720;
    const height = titleHeight + padding * 2 + lines.length * lineHeight;
    canvas.width = width;
    canvas.height = height;

    // Fondo degradado oscuro
    const grad = ctx.createLinearGradient(0, 0, 0, height);
    grad.addColorStop(0, "#0a0a0a");
    grad.addColorStop(1, "#1a0a2e");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, width, height);

    // Borde dorado
    ctx.strokeStyle = "#facc15";
    ctx.lineWidth = 3;
    ctx.strokeRect(8, 8, width - 16, height - 16);

    // Tipografía
    ctx.fillStyle = "#facc15";
    ctx.font = "bold 22px 'Inter', system-ui, sans-serif";
    ctx.fillText("JG MODELS — CIERRE DE TURNO", padding, padding + 22);

    ctx.fillStyle = "#ffffff";
    ctx.font = "16px 'Inter', system-ui, sans-serif";

    let y = titleHeight + padding;
    for (const line of lines) {
      // Líneas con $ las dibujamos en amarillo destacado
      if (line.includes("Total recaudado")) {
        ctx.fillStyle = "#22c55e";
        ctx.font = "bold 18px 'Inter', system-ui, sans-serif";
      } else if (line.startsWith("📊") || line.startsWith("📂") || line.startsWith("👑") || line.startsWith("📌")) {
        ctx.fillStyle = "#facc15";
        ctx.font = "bold 16px 'Inter', system-ui, sans-serif";
      } else {
        ctx.fillStyle = "#e5e5e5";
        ctx.font = "15px 'Inter', system-ui, sans-serif";
      }
      ctx.fillText(line, padding, y);
      y += lineHeight;
    }

    return canvas.toDataURL("image/png");
  };

  const handleDownloadImage = () => {
    const url = generateImage();
    if (!url) {
      toast.error("No se pudo generar la imagen");
      return;
    }
    const a = document.createElement("a");
    a.href = url;
    a.download = `cierre-turno-${report?.date || "reporte"}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setPreviewUrl(url);
    toast.success("Imagen descargada");
  };

  if (!report) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        data-testid="whatsapp-report-dialog"
        className="w-[95vw] max-w-lg bg-zinc-900 border-zinc-800 max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Share2 className="w-5 h-5 text-green-400" />
            Reportar cierre de turno
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-gray-400 text-sm">
            Tu reporte quedó guardado en el CRM. Ahora puedes compartir el resumen en WhatsApp:
          </p>

          <Textarea
            data-testid="whatsapp-report-text"
            value={messageText}
            readOnly
            rows={14}
            className="bg-zinc-800 border-zinc-700 text-white text-sm font-mono"
          />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Button
              data-testid="whatsapp-copy-btn"
              onClick={handleCopy}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copiar texto
            </Button>
            <Button
              data-testid="whatsapp-image-btn"
              onClick={handleDownloadImage}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Descargar imagen
            </Button>
            <Button
              data-testid="whatsapp-share-btn"
              onClick={handleShare}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Compartir
            </Button>
          </div>

          {previewUrl && (
            <div>
              <p className="text-gray-400 text-xs mb-2">Vista previa de la imagen:</p>
              <img
                src={previewUrl}
                alt="Cierre de turno"
                className="w-full rounded-lg border border-zinc-700"
              />
            </div>
          )}

          {/* Canvas oculto para generar la imagen */}
          <canvas ref={canvasRef} className="hidden" />
        </div>
      </DialogContent>
    </Dialog>
  );
}
